import { DateTime } from 'luxon';
import { ReleaseResourceAllocationType } from '~/types';
import { compareISOStrings } from '~/modules/common/dates/compare';

export const getImpactedTasksByReleasingResources = ({
  resources,
  allocations,
  releaseDate: asOfDate,
  releaseType
}) => {
  if (resources.length === 0) {
    return {
      selectedUsersForReleasing: [],
      impactedTasks: []
    };
  }

  const impactedResources = resources.filter(resource =>
    allocations.some(
      allocation => allocation.isSelected && allocation.user.uri === resource.id
    )
  );

  const { impactedTasks } = impactedResources.reduce(
    (acc, resource) => {
      const resourceAllocation = allocations.find(
        allocation => allocation.user.uri === resource.id
      );

      const tasks = getTasksWithImpactedTaskOwnersUsingAllocationAndTaskRoleMatch(
        {
          asOfDate,
          releaseType,
          allocations: [resourceAllocation],
          resourceUser: resource
        }
      );

      acc.impactedTasks.push(...tasks);

      return acc;
    },
    { impactedTasks: [] }
  );

  return {
    selectedUsersForReleasing: impactedResources,
    impactedTasks
  };
};

export const isTaskEndsAfterAsOfDate = ({ asOfDate, task }) => {
  const { endDate } = task;

  return !endDate || compareISOStrings(asOfDate, endDate) === -1;
};

export const isAsOfDateFallingBetweenTaskStartAndEndDate = ({
  asOfDate,
  task
}) => {
  const { endDate, startDate } = task;

  if (!endDate && !startDate) return true;

  const start = startDate
    ? DateTime.fromISO(startDate, { setZone: true })
    : null;
  const end = endDate ? DateTime.fromISO(endDate, { setZone: true }) : null;
  const dateToCompare = DateTime.fromISO(asOfDate, { setZone: true });

  if (!start) return dateToCompare <= end;

  if (!end) return start <= dateToCompare;

  return start <= dateToCompare && dateToCompare <= end;
};

export const getTasksWithImpactedTaskOwnersUsingAllocationAndTaskRoleMatch = ({
  asOfDate,
  releaseType,
  allocations,
  resourceUser
}) => {
  const { assignedTasks = [] } = resourceUser ?? {};

  const isAllAssignedTasksImpacted = allocations.every(
    allocation =>
      allocation.isSelected &&
      (releaseType === ReleaseResourceAllocationType.Entirely ||
        compareISOStrings(asOfDate, allocation.startDate) === -1)
  );

  if (isAllAssignedTasksImpacted) return assignedTasks;

  const { allocationCounts, selectedAllocationCounts } = allocations.reduce(
    (acc, { role, allocationRoleId, isSelected }) => {
      const roleId = role?.id || allocationRoleId;

      if (roleId) {
        acc.allocationCounts[roleId] = (acc.allocationCounts[roleId] ?? 0) + 1;

        if (isSelected) {
          acc.selectedAllocationCounts[roleId] =
            (acc.selectedAllocationCounts[roleId] ?? 0) + 1;
        }
      }

      return acc;
    },
    { allocationCounts: {}, selectedAllocationCounts: {} }
  );

  const impactedTasks =
    resourceUser?.allocationCount === 1
      ? assignedTasks
      : assignedTasks.filter(({ assignedRoleUri }) => {
          if (assignedRoleUri) {
            const selectedCount =
              selectedAllocationCounts[assignedRoleUri] ?? 0;

            return (
              selectedCount > 0 &&
              allocationCounts[assignedRoleUri] === selectedCount
            );
          }

          return false;
        });

  return releaseType === ReleaseResourceAllocationType.Asof
    ? impactedTasks.filter(task =>
        isTaskEndsAfterAsOfDate({
          asOfDate,
          task
        })
      )
    : impactedTasks;
};
